/**
 * domains/market/engines/setup2/readiness.ts — MER Algo Footprints v7.1H
 *
 * Readiness, penalties, disqualifiers, and full lifecycle state transitions.
 * Session phase uses the canonical shared utility — no inline UTC-offset hacks.
 */

import {
  ReadinessBreakdown, Penalty, Disqualifier,
  SetupClass, SetupDirection, SetupState,
  ThresholdSnapshot, DEFAULT_THRESHOLDS,
} from './types';
import type { SMCResult, RegimeResult, TrapSignal, TrendResult } from '../../../../shared/types';
import { getSessionPhaseFromCandle, isHighActivityPhase, SessionPhase } from '../../../../shared/utils/session';

// ─── Readiness ────────────────────────────────────────────────────────────────
// Asks: "Is this setup executable RIGHT NOW?"
// Independently scored from quality — a great setup can have low readiness.

export interface ReadinessInput {
  hasTrigger:          boolean;
  triggerFreshness:    number;    // 0–1
  triggerConfidence:   'none' | 'forming' | 'partial' | 'confirmed';
  hasConfirmation:     boolean;
  atExecutionZone:     boolean;
  zoneProximityPct:    number;    // 0–1 (1 = at zone, 0 = far away)
  stopLevelClear:      boolean;
  rr:                  number | null;
  rvol:                number;
  dispFreshness:       number;    // 0–1
  lastCandleTs:        number;    // Unix ms — for session phase derivation
  thresholds?:         Partial<ThresholdSnapshot>;
}

export function scoreReadiness(inp: ReadinessInput): ReadinessBreakdown {
  const thr = { ...DEFAULT_THRESHOLDS, ...inp.thresholds };

  // ── Graded trigger check (not binary) ────────────────────────────────────
  const trigger_confirmed =
    inp.triggerConfidence === 'confirmed' && inp.triggerFreshness >= 0.5;
  const confirmation_present = inp.hasConfirmation;

  // ── Execution zone — graded by proximity (not binary) ────────────────────
  const execution_zone_active = inp.atExecutionZone && inp.zoneProximityPct >= 0.6;

  // ── Invalidation clarity ──────────────────────────────────────────────────
  const invalidation_clear = inp.stopLevelClear;

  // ── R/R viability ─────────────────────────────────────────────────────────
  const rr_viable = inp.rr !== null && inp.rr >= thr.min_rr;

  // ── Timing quality via canonical session utility ──────────────────────────
  const phase: SessionPhase = getSessionPhaseFromCandle(inp.lastCandleTs);
  const timing_quality: 'good' | 'marginal' | 'poor' =
    isHighActivityPhase(phase)                         ? 'good'
    : (phase === 'mid-morning' || phase === 'afternoon') ? 'marginal'
    : phase === 'midday'                                 ? 'marginal'
    : inp.rvol >= 1.2                                   ? 'marginal'
    :                                                      'poor';

  // ── Readiness score — graded contributions ────────────────────────────────
  let readiness_score = 0;

  // Trigger — graded by confidence level
  readiness_score += inp.triggerConfidence === 'confirmed' ? 25
                   : inp.triggerConfidence === 'partial'   ? 15
                   : inp.triggerConfidence === 'forming'   ? 7
                   : 0;

  readiness_score += confirmation_present   ? 20 : 0;

  // Zone proximity — graded
  readiness_score += inp.zoneProximityPct >= 0.9 ? 20
                   : inp.zoneProximityPct >= 0.6 ? 14
                   : inp.zoneProximityPct >= 0.3 ? 6
                   : 0;

  readiness_score += invalidation_clear     ? 15 : 0;
  readiness_score += rr_viable              ? 15 : 0;
  readiness_score += timing_quality === 'good'     ? 5
                   : timing_quality === 'marginal' ? 2
                   :                                 0;

  // Displacement freshness bonus (execution timing)
  readiness_score += Math.round(inp.dispFreshness * 5);

  return {
    trigger_confirmed,
    confirmation_present,
    execution_zone_active,
    invalidation_clear,
    rr_viable,
    timing_quality,
    readiness_score: Math.min(100, readiness_score),
  };
}

// ─── Penalties ────────────────────────────────────────────────────────────────

export interface PenaltyInput {
  smc:           SMCResult | null;
  regime:        RegimeResult | null;
  trend:         TrendResult | null;
  traps:         TrapSignal[];
  direction:     SetupDirection;
  setupClass:    SetupClass;
  rvol:          number;
  dispFreshness: number;
  candles_len:   number;
}

export function evaluatePenalties(inp: PenaltyInput): Penalty[] {
  const p: Penalty[] = [];
  const smcBias  = inp.smc?.bias;
  const trendDir = inp.trend?.trendDirection;

  // Countertrend
  if ((inp.direction === 'LONG'  && smcBias === 'bearish') ||
      (inp.direction === 'SHORT' && smcBias === 'bullish'))
    p.push({ reason: 'Counter SMC bias', points: 12 });

  if ((inp.direction === 'LONG'  && trendDir === 'bearish') ||
      (inp.direction === 'SHORT' && trendDir === 'bullish'))
    p.push({ reason: 'Counter trend direction', points: 8 });

  // Trap severity
  const highTraps = inp.traps.filter(t => t.severity === 'High');
  const medTraps  = inp.traps.filter(t => t.severity === 'Medium');
  if (highTraps.length) p.push({ reason: `High-severity trap: ${highTraps[0].type}`, points: 18 });
  else if (medTraps.length) p.push({ reason: 'Medium-severity trap active', points: 8 });

  // RVOL
  if (inp.rvol < 0.7) p.push({ reason: 'Very low relative volume (< 0.7×)', points: 10 });
  else if (inp.rvol < 0.9) p.push({ reason: 'Below-average relative volume', points: 5 });

  // Stale structure
  const lastSt = inp.smc?.lastBOS?.candleIdx ?? inp.smc?.lastCHOCH?.candleIdx ?? null;
  if (lastSt !== null && inp.candles_len - 1 - lastSt > 20)
    p.push({ reason: 'Stale structure event (> 20 candles ago)', points: 10 });

  // Stale displacement
  if (inp.dispFreshness < 0.3) p.push({ reason: 'Stale displacement signal', points: 8 });

  // P/D zone misalignment
  const pd = inp.smc?.premiumDiscount;
  if (pd) {
    const inWrongZone = (inp.direction === 'LONG' && pd.zone === 'premium') ||
                        (inp.direction === 'SHORT' && pd.zone === 'discount');
    if (inWrongZone)
      p.push({ reason: `Price in ${pd.zone} — wrong side for ${inp.direction}`, points: 10 });
  }

  return p;
}

// ─── Disqualifiers ────────────────────────────────────────────────────────────

export interface DisqualifierInput {
  smc:            SMCResult | null;
  regime:         RegimeResult | null;
  traps:          TrapSignal[];
  direction:      SetupDirection;
  setupClass:     SetupClass;
  hasTrigger:     boolean;
  stopLevelClear: boolean;
  rr:             number | null;
  thresholds?:    Partial<ThresholdSnapshot>;
}

export function evaluateDisqualifiers(inp: DisqualifierInput): Disqualifier[] {
  const disqs: Disqualifier[] = [];
  const thr = { ...DEFAULT_THRESHOLDS, ...inp.thresholds };

  if (!inp.hasTrigger)
    disqs.push({ rule: 'NO_TRIGGER', fatal: true,
      reason: 'No specific entry trigger identified' });

  if (!inp.stopLevelClear)
    disqs.push({ rule: 'INVALIDATION_UNCLEAR', fatal: true,
      reason: 'Stop / invalidation level not determinable' });

  if (inp.rr !== null && inp.rr < thr.min_rr)
    disqs.push({ rule: 'RR_BELOW_MINIMUM', fatal: true,
      reason: `R/R ${inp.rr.toFixed(2)} is below minimum ${thr.min_rr}` });

  // Extreme trap — fatal only when trap is on SAME side as trade direction
  const directionalTraps = inp.traps.filter(t =>
    t.severity === 'High' && (
      (inp.direction === 'LONG'  && t.side === 'bull') ||
      (inp.direction === 'SHORT' && t.side === 'bear')
    )
  );
  if (directionalTraps.length)
    disqs.push({ rule: 'EXTREME_TRAP_RISK', fatal: true,
      reason: `Extreme ${inp.direction}-side trap: ${directionalTraps[0].type}` });

  // Hard context misalignment
  const noStructure = !inp.smc?.lastBOS && !inp.smc?.lastCHOCH;
  const regimeOpp   = (inp.direction === 'LONG'  && inp.regime?.posture === 'bearish') ||
                      (inp.direction === 'SHORT' && inp.regime?.posture === 'bullish');
  if (noStructure && regimeOpp)
    disqs.push({ rule: 'HARD_CONTEXT_MISALIGNMENT', fatal: true,
      reason: 'No structural confirmation and regime opposes direction' });

  // Non-fatal
  if ((inp.regime?.confidence ?? 0) < 30)
    disqs.push({ rule: 'LOW_REGIME_CONFIDENCE', fatal: false,
      reason: `Regime confidence ${inp.regime?.confidence ?? 0}% — context unclear` });

  return disqs;
}

// ─── Lifecycle state transition ────────────────────────────────────────────────
// Full lifecycle: DETECTED → QUALIFIED → DEVELOPING → READY → (ACTIVE) → INVALIDATED/EXPIRED
// ACTIVE is not auto-set here — requires external trade-entry signal.
// INVALIDATED/EXPIRED are evaluated from staleness + structural break.

export interface StateTransitionParams {
  setupScore:        number;
  readinessScore:    number;
  hasFatalDisq:      boolean;
  hasTrigger:        boolean;
  triggerConfidence: 'none' | 'forming' | 'partial' | 'confirmed';
  hasConfirmation:   boolean;
  setupClass:        SetupClass;
  structureAge:      number;     // candles since last BOS/CHOCH
  setupAge:          number;     // candles since setup was first detected
  thresholds?:       Partial<ThresholdSnapshot>;
}

export interface SetupStateResult {
  state:  SetupState;
  reason: string;
}

export function deriveSetupState(params: StateTransitionParams): SetupStateResult {
  const thr = { ...DEFAULT_THRESHOLDS, ...params.thresholds };

  // ── ACTIVE: deterministic proxy for live-window setup ───────────────────────
  // Fires when: (1) all quality+readiness gates are well-exceeded,
  //             (2) trigger is confirmed and fresh (setupAge ≤ 2 candles from event),
  //             (3) no fatal disqualifier.
  // This is the best available deterministic proxy without external trade-entry signals.
  // DESIGN NOTE: ACTIVE is intentionally conservative here — the proxy requires high
  // bar thresholds (score ≥ 80, readiness ≥ 85). A future broker-connected path could
  // lower this threshold once entry acknowledgment is available, but that requires an
  // execution integration that is outside this system's non-executing design scope.
  if (!params.hasFatalDisq &&
      params.setupScore     >= 80 &&
      params.readinessScore >= 85 &&
      params.triggerConfidence === 'confirmed' &&
      params.setupAge <= 2)   // ≤2 candle-intervals from primary event = genuinely fresh
    return { state: 'ACTIVE', reason: `Deterministic ACTIVE proxy: score=${params.setupScore} readiness=${params.readinessScore} trigger=${params.triggerConfidence} age=${params.setupAge}` };

  // ── INVALIDATED: explicit break/failure conditions ────────────────────────────
  // Must reflect actual structure failure — not just weakness.
  // Conditions (any one triggers):
  //   A. Structure completely absent (structureAge > 40 candles) — basis gone
  //   B. Score collapsed to detection floor AND structure is stale (>25)
  //   C. Trigger decayed to 'none' AND setup has aged past actionable window
  //   D. triggerDecayed early (setupAge > 5) with already-low score → setup failed fast
  const triggerDecayed = params.triggerConfidence === 'none';
  const ageWindow = 8; // candle-interval units before a confirmed trigger is considered failed
  if (params.structureAge > 40 ||
      (params.structureAge > 25 && params.setupScore < 30) ||
      (triggerDecayed && params.setupAge > ageWindow) ||
      (triggerDecayed && params.setupAge > 5 && params.setupScore < 40)) {
    const invReason = params.structureAge > 40
      ? `Structure basis gone (structureAge=${params.structureAge})`
      : (params.structureAge > 25 && params.setupScore < 30)
        ? `Score collapsed to ${params.setupScore} with stale structure (age=${params.structureAge})`
        : params.setupAge > 5 && params.setupScore < 40
          ? `Trigger decayed early (setupAge=${params.setupAge} score=${params.setupScore})`
          : `Trigger failed past window (setupAge=${params.setupAge})`;
    return { state: 'INVALIDATED', reason: invReason };
  }

  // ── EXPIRED: missed timing window ────────────────────────────────────────────
  // A setup EXPIRES when the trigger was present but the window closed without activation.
  // Distinct from INVALIDATED: structure is still valid, trigger just aged out.
  // Conditions: trigger existed (partial or confirmed), but setupAge > window AND
  //             structure is stale enough that a new evaluation would be more current.
  const triggerWasPresent = params.triggerConfidence !== 'none';
  const windowPassed      = params.setupAge > 10;  // 10 candle-intervals = window closed
  if (triggerWasPresent && windowPassed &&
      params.structureAge > 15 && params.setupScore < 60)
    return { state: 'EXPIRED', reason: `Timing window closed: trigger was ${params.triggerConfidence} but setupAge=${params.setupAge} structureAge=${params.structureAge} score=${params.setupScore}` };

  // ── QUALIFIED but fatally blocked ────────────────────────────────────────────
  if (params.hasFatalDisq)
    return { state: 'QUALIFIED', reason: `Fatal disqualifier present — blocked from READY` };

  // ── DEVELOPING: direction clear, trigger forming ──────────────────────────────
  if (params.triggerConfidence === 'none' || params.triggerConfidence === 'forming')
    return { state: 'DEVELOPING', reason: `Trigger ${params.triggerConfidence} — awaiting confirmation` };

  // ── READY: all quality + readiness gates pass ─────────────────────────────────
  if (params.setupScore     >= thr.min_setup_score     &&
      params.readinessScore >= thr.min_readiness_score &&
      params.triggerConfidence === 'confirmed'          &&
      !params.hasFatalDisq)
    return { state: 'READY', reason: `All gates passed: score=${params.setupScore} readiness=${params.readinessScore} trigger=confirmed` };

  // ── DEVELOPING: trigger partial or quality building ───────────────────────────
  if (params.setupScore >= 50 &&
      (params.hasTrigger || params.triggerConfidence === 'partial'))
    return { state: 'DEVELOPING', reason: `Score=${params.setupScore} trigger=${params.triggerConfidence} — developing toward READY` };

  // ── QUALIFIED: detected and minimally screened ────────────────────────────────
  if (params.setupScore >= 35)
    return { state: 'QUALIFIED', reason: `Score=${params.setupScore} detected but not yet DEVELOPING` };

  return { state: 'DETECTED', reason: `Score=${params.setupScore} below qualification threshold` };
}
